import { useEffect, useRef, useState, type ChangeEvent, type FormEvent } from "react";
import { useNavigate } from "react-router-dom";
import AppShell from "@/components/feature/AppShell";
import { todayMeals, todaySummary } from "@/mocks/meals";

type InputMode = "image" | "text" | "voice";

export default function Home() {
  const navigate = useNavigate();
  const [mode, setMode] = useState<InputMode>("image");
  const [recording, setRecording] = useState(false);
  const [toast, setToast] = useState<string | null>(null);
  const [manualFood, setManualFood] = useState("");
  const [showFormat, setShowFormat] = useState(false);
  const [cameraActive, setCameraActive] = useState(false);
  const [facingMode, setFacingMode] = useState<"environment" | "user">("environment");

  const recordingRef = useRef(false);
  const imageInputRef = useRef<HTMLInputElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const toastTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const navTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const progress = Math.min(100, Math.round((todaySummary.intake / todaySummary.target) * 100));

  useEffect(() => {
    return () => {
      if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
      if (navTimerRef.current) clearTimeout(navTimerRef.current);
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((t) => t.stop());
      }
    };
  }, []);

  const showToast = (msg: string) => {
    setToast(msg);
    if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
    toastTimerRef.current = setTimeout(() => setToast(null), 1600);
  };

  const goRecognize = (state?: Record<string, unknown>) => {
    navTimerRef.current = setTimeout(() => navigate("/recognize", { state }), 600);
  };

  const stopCamera = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
    setCameraActive(false);
  };

  const failCamera = () => {
    stopCamera();
    showToast("摄像头未开启或未安装，请重新选择输入方式");
    navTimerRef.current = setTimeout(() => setMode("text"), 1400);
  };

  const handleCapture = async () => {
    if (cameraActive && videoRef.current) {
      showToast("拍照识别中...");
      stopCamera();
      goRecognize({ mode: "image", imageName: "拍照食物照片" });
      return;
    }
    if (!navigator.mediaDevices?.getUserMedia) {
      failCamera();
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode },
        audio: false,
      });
      streamRef.current = stream;
      setCameraActive(true);
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play();
      }
    } catch {
      failCamera();
    }
  };

  const handleSwitchCamera = async () => {
    if (!streamRef.current) {
      showToast("请先开启摄像头");
      return;
    }
    const next = facingMode === "environment" ? "user" : "environment";
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: next },
        audio: false,
      });
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = stream;
      setFacingMode(next);
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play();
      }
    } catch {
      showToast("切换镜头失败");
    }
  };

  const handleImagePick = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    showToast("已选择图片，正在识别...");
    goRecognize({ mode: "image", imageName: file.name });
    e.target.value = "";
  };

  const handleManualSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const q = manualFood.trim();
    if (!q) {
      showToast("请输入食物名称");
      return;
    }
    showToast("正在识别...");
    setManualFood("");
    goRecognize({ mode: "text", query: q });
  };

  const handleVoiceDown = () => {
    recordingRef.current = true;
    setRecording(true);
  };
  const handleVoiceUp = () => {
    if (!recordingRef.current) return;
    recordingRef.current = false;
    setRecording(false);
    showToast("正在识别语音...");
    // 模拟语音识别结果，实际可接入语音识别服务
    goRecognize({ mode: "voice", query: "一碗米饭、一份西红柿炒蛋、一个苹果" });
  };

  const modeItems: { key: InputMode; icon: string; title: string; desc: string }[] = [
    { key: "image", icon: "ri-image-line", title: "图片识物", desc: "上传食物照片" },
    { key: "text", icon: "ri-keyboard-line", title: "手工输入", desc: "文字描述食物" },
    { key: "voice", icon: "ri-mic-line", title: "语音输入", desc: "按住说出食物" },
  ];

  return (
    <AppShell>
      {/* Header */}
      <header className="px-5 pt-6 pb-4 flex items-center justify-between">
        <div>
          <p className="text-xs text-gray-400">你好，今天也要好好吃饭</p>
          <h1 className="text-xl font-semibold text-gray-900 mt-0.5">智能饮食助手</h1>
        </div>
        <button className="w-10 h-10 rounded-full bg-white border border-warm-200 flex items-center justify-center cursor-pointer">
          <i className="ri-user-smile-line text-gray-500 text-lg"></i>
        </button>
      </header>

      {/* Input mode selector */}
      <section className="px-5 mt-1">
        <p className="text-xs text-gray-400 mb-2">选择输入方式</p>
        <div className="grid grid-cols-3 gap-2">
          {modeItems.map((m) => {
            const active = mode === m.key;
            return (
              <button
                key={m.key}
                onClick={() => setMode(m.key)}
                className={`rounded-card p-4 border flex flex-col items-center gap-2 transition-all cursor-pointer whitespace-nowrap ${
                  active
                    ? "bg-mint-50 border-mint-400 text-mint-600"
                    : "bg-white border-warm-200 text-gray-500 hover:border-mint-300"
                }`}
              >
                <span className="w-10 h-10 flex items-center justify-center rounded-full bg-white">
                  <i className={`${m.icon} text-xl`}></i>
                </span>
                <span className="text-sm font-medium text-gray-900">{m.title}</span>
                <span className="text-[11px] text-gray-400">{m.desc}</span>
              </button>
            );
          })}
        </div>
      </section>

      {/* Input area */}
      <section className="px-5 mt-4">
        {mode === "image" && (
          <div className="bg-white rounded-card border border-warm-200 p-2">
            <section className="rounded-card overflow-hidden relative bg-gray-900" style={{ height: 320 }}>
              {cameraActive ? (
                <video
                  ref={videoRef}
                  playsInline
                  muted
                  className="w-full h-full object-cover object-top"
                />
              ) : (
                <img
                  src="https://readdy.ai/api/search-image?query=Overhead%20soft%20focused%20photo%20of%20a%20healthy%20colorful%20breakfast%20bowl%20on%20a%20warm%20beige%20table%2C%20oats%2C%20blueberries%2C%20boiled%20egg%2C%20mint%20leaves%2C%20natural%20window%20light%2C%20minimalist%20wellness%20food%20photography%2C%20clean%20neutral%20background%2C%20high%20detail%2C%20airy%20composition&width=960&height=1280&seq=diet-camera-viewfinder-1&orientation=portrait"
                  alt="camera viewfinder"
                  className="w-full h-full object-cover object-top"
                />
              )}

              <div className="absolute inset-0 bg-gradient-to-b from-black/20 via-transparent to-black/40"></div>

              <div className="absolute inset-6 pointer-events-none">
                <span className="absolute top-0 left-0 w-6 h-6 border-t-2 border-l-2 border-white/90 rounded-tl"></span>
                <span className="absolute top-0 right-0 w-6 h-6 border-t-2 border-r-2 border-white/90 rounded-tr"></span>
                <span className="absolute bottom-0 left-0 w-6 h-6 border-b-2 border-l-2 border-white/90 rounded-bl"></span>
                <span className="absolute bottom-0 right-0 w-6 h-6 border-b-2 border-r-2 border-white/90 rounded-br"></span>
              </div>

              <div className="absolute top-4 left-1/2 -translate-x-1/2 bg-black/40 text-white text-xs px-3 py-1 rounded-full whitespace-nowrap">
                {cameraActive ? "对准食物，点击快门拍照" : "将食物置于取景框内"}
              </div>

              <div className="absolute bottom-4 left-0 right-0 flex items-center justify-center gap-8">
                <button
                  onClick={() => imageInputRef.current?.click()}
                  className="w-11 h-11 rounded-full bg-white/25 backdrop-blur flex items-center justify-center text-white cursor-pointer"
                >
                  <i className="ri-image-line text-lg"></i>
                </button>
                <button
                  onClick={handleCapture}
                  className="w-16 h-16 rounded-full bg-white flex items-center justify-center cursor-pointer ring-4 ring-white/40"
                >
                  <span className="w-12 h-12 rounded-full bg-mint-400 flex items-center justify-center">
                    <i className="ri-camera-fill text-white text-2xl"></i>
                  </span>
                </button>
                <button
                  onClick={handleSwitchCamera}
                  className="w-11 h-11 rounded-full bg-white/25 backdrop-blur flex items-center justify-center text-white cursor-pointer"
                >
                  <i className="ri-refresh-line text-lg"></i>
                </button>
              </div>
            </section>
            <p className="text-xs text-gray-400 text-center py-2">点快门拍照，或从相册选择已有照片</p>
          </div>
        )}

        {mode === "text" && (
          <div className="bg-white rounded-card border border-warm-200 p-4">
            <form onSubmit={handleManualSubmit} className="flex flex-col sm:flex-row items-stretch gap-2">
              <input
                type="text"
                value={manualFood}
                onChange={(e) => setManualFood(e.target.value)}
                placeholder="如：一碗米饭、半份西红柿炒蛋"
                className="flex-1 h-12 rounded-btn bg-warm-50 border border-warm-200 px-4 text-sm text-gray-800 outline-none focus:border-mint-400 placeholder:text-gray-400"
              />
              <button
                type="submit"
                className="h-12 px-6 rounded-btn bg-mint-400 text-white text-sm font-medium whitespace-nowrap cursor-pointer"
              >
                识别
              </button>
            </form>
            <button
              onClick={() => setShowFormat(!showFormat)}
              className="text-xs text-mint-500 flex items-center gap-1 cursor-pointer whitespace-nowrap mt-3"
            >
              查看输入格式标准 <i className={showFormat ? "ri-arrow-up-s-line" : "ri-arrow-down-s-line"}></i>
            </button>
          </div>
        )}

        {mode === "voice" && (
          <div className="bg-white rounded-card border border-warm-200 p-5">
            <button
              onMouseDown={handleVoiceDown}
              onMouseUp={handleVoiceUp}
              onMouseLeave={handleVoiceUp}
              onTouchStart={handleVoiceDown}
              onTouchEnd={handleVoiceUp}
              className={`w-full h-28 rounded-lg flex flex-col items-center justify-center gap-2 font-medium whitespace-nowrap transition-all cursor-pointer select-none ${
                recording
                  ? "bg-mint-500 text-white scale-[0.99]"
                  : "bg-warm-50 border border-warm-200 text-gray-700"
              }`}
            >
              <span className="w-10 h-10 flex items-center justify-center">
                <i className={`${recording ? "ri-mic-fill" : "ri-mic-line"} text-2xl`}></i>
              </span>
              <span className="text-sm">{recording ? "松开结束 · 正在聆听..." : "按住说话，说出这顿吃了什么"}</span>
            </button>
          </div>
        )}

        {showFormat && (
          <div className="mt-3 bg-mint-50 border border-mint-100 rounded-card p-4">
            <p className="text-xs font-medium text-mint-600 mb-2">输入格式标准</p>
            <ul className="text-xs text-gray-600 space-y-1.5 leading-relaxed">
              <li>1. 格式为「数量 + 食物名称」，如「一碗米饭」「两个苹果」</li>
              <li>2. 多个食物用顿号「、」或逗号分隔，如「一碗米饭、半份西红柿炒蛋」</li>
              <li>3. 数量可用「一/两/半」或数字，单位支持 碗/份/个/杯/根/块/片/盒/串/颗/把</li>
              <li>4. 也可直接写重量，如「米饭 200克」</li>
              <li>5. 类别（主餐 / 菜 / 汤 / 水果 / 甜品）会自动识别</li>
            </ul>
          </div>
        )}
      </section>

      {/* Today summary */}
      <section className="px-5 mt-6">
        <div className="bg-white rounded-card p-4 border border-warm-200">
          <div className="flex items-end justify-between">
            <div>
              <p className="text-xs text-gray-400">今日摄入</p>
              <p className="text-2xl font-semibold text-gray-900 mt-0.5">
                {todaySummary.intake}
                <span className="text-sm text-gray-400 font-normal"> / {todaySummary.target} kcal</span>
              </p>
            </div>
            <button
              onClick={() => navigate("/analysis")}
              className="text-xs text-mint-500 flex items-center gap-1 cursor-pointer whitespace-nowrap"
            >
              查看分析 <i className="ri-arrow-right-s-line"></i>
            </button>
          </div>
          <div className="mt-3 h-2 bg-warm-100 rounded-full overflow-hidden">
            <div className="h-full bg-mint-400 rounded-full transition-all" style={{ width: `${progress}%` }}></div>
          </div>
          <div className="mt-3 grid grid-cols-3 gap-2 text-center">
            {[
              { label: "蛋白质", value: todaySummary.protein, unit: "g" },
              { label: "碳水", value: todaySummary.carbs, unit: "g" },
              { label: "脂肪", value: todaySummary.fat, unit: "g" },
            ].map((n) => (
              <div key={n.label} className="bg-warm-50 rounded-lg py-2">
                <p className="text-xs text-gray-400">{n.label}</p>
                <p className="text-sm font-medium text-gray-800 mt-0.5">
                  {n.value}
                  <span className="text-[11px] text-gray-400"> {n.unit}</span>
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Today meals */}
      <section className="px-5 mt-6">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-base font-semibold text-gray-900">今日三餐</h2>
          <button
            onClick={() => navigate("/records")}
            className="text-xs text-gray-400 flex items-center gap-1 cursor-pointer whitespace-nowrap"
          >
            全部记录 <i className="ri-arrow-right-s-line"></i>
          </button>
        </div>
        <div className="space-y-3">
          {todayMeals.map((m) => {
            const p = Math.min(100, Math.round((m.calories / m.target) * 100));
            const empty = m.calories === 0;
            return (
              <div
                key={m.key}
                onClick={() => navigate("/records")}
                className="bg-white rounded-card p-4 border border-warm-200 cursor-pointer"
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <span className="w-10 h-10 flex items-center justify-center rounded-full bg-mint-50 text-mint-500">
                      <i className={`${m.icon} text-lg`}></i>
                    </span>
                    <div>
                      <p className="text-sm font-medium text-gray-900">{m.name}</p>
                      <p className="text-xs text-gray-400 mt-0.5">
                        {empty ? "还没有记录，去添加一下吧" : m.items.join(" · ")}
                      </p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-sm font-semibold text-gray-900">
                      {m.calories}
                      <span className="text-[11px] text-gray-400 font-normal"> kcal</span>
                    </p>
                    <p className="text-[11px] text-gray-400 mt-0.5">目标 {m.target}</p>
                  </div>
                </div>
                <div className="mt-3 h-1.5 bg-warm-100 rounded-full overflow-hidden">
                  <div
                    className={`h-full rounded-full ${empty ? "bg-warm-200" : "bg-mint-400"}`}
                    style={{ width: `${p}%` }}
                  ></div>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* AI Tip */}
      <section className="px-5 mt-6">
        <div className="rounded-card p-4 bg-mint-50 border border-mint-100 flex gap-3">
          <span className="w-8 h-8 flex items-center justify-center rounded-full bg-mint-400 text-white shrink-0">
            <i className="ri-sparkling-2-line"></i>
          </span>
          <div>
            <p className="text-sm font-medium text-mint-600">AI 小助手</p>
            <p className="text-xs text-gray-600 mt-1 leading-relaxed">
              今天蛋白质摄入略低，晚餐可以加一份鸡胸肉或豆腐，帮助达到营养均衡目标。
            </p>
          </div>
        </div>
      </section>

      <input
        ref={imageInputRef}
        type="file"
        accept="image/*"
        onChange={handleImagePick}
        className="hidden"
      />

      {toast && (
        <div className="fixed bottom-24 left-1/2 -translate-x-1/2 bg-gray-900/85 text-white text-xs px-4 py-2 rounded-full z-50 whitespace-nowrap">
          {toast}
        </div>
      )}
    </AppShell>
  );
}